import dayjs from "dayjs";
import { ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, matchPath, useNavigate } from "react-router-dom";
import { Modal } from "@/app/components/ui/modal.jsx";
import { slugifyURL } from "@/app/handlers/urlFunctions";
import { stripHTML } from "@/app/handlers/validationFunctions.js";
import { useFeatureFlag } from "@/app/hooks/useFeatureFlag.jsx";
import useIsLoggedIn from "../../../../../hooks/use-IsLoggedIn";
import DSPModal from "../../../../JobsPage/components/jobApplication/DSPModal";
import GenerateSingleJobImage from "../../../../Social/GenerateSingleJobImage";
import ApplyModal from "../ApplyModal";
import Loader from "../Loader";
// Components
import { ApplicationProcess } from "./components/ApplicationProcess";
import { CompanyCard } from "./components/CompanyCard";
import { FitCheckSection } from "./components/FitCheckSection";
import { JobActionButtons } from "./components/JobActionButtons";
import { JobDescription } from "./components/JobDescription";
import { JobHeader } from "./components/JobHeader";
import { JobMetadata } from "./components/JobMetadata";
import { MoreRolesFromCompany } from "./components/MoreRolesFromCompany";
import { RoleExplainerSection } from "./components/RoleExplainerSection";
// Hooks
import { useFitCheckFlow } from "./hooks/useFitCheckFlow";
import { useJobApplication } from "./hooks/useJobApplication";
import { useJobDetails } from "./hooks/useJobDetails";
import { useJobInteractions } from "./hooks/useJobInteractions";
import { RoleExplainerSurface, useJobPageAnalytics } from "./hooks/useJobPageAnalytics";
import { useRoleExplainer } from "./hooks/useRoleExplainer";
import { useRoleExplainerViewState } from "./hooks/useRoleExplainerViewState";
// Utils
import { isJobExpired, JobDetails, toLowerCaseCapitalize } from "./utils/helpers";

interface ViewJobProps {
	data?: { _id?: string };
	setData?: (data: JobDetails) => void;
	setShowJobDetailsModal?: (show: boolean) => void;
	/**
	 * Which surface this instance renders on, reported with Role Explainer events.
	 * Defaults from the `data` prop: the standalone page reads the job from the URL,
	 * every drawer host passes the job in.
	 */
	surface?: RoleExplainerSurface;
}

function ViewJob({ data, setData, setShowJobDetailsModal, surface }: ViewJobProps) {
	const baseUrl = `${window.location.origin}/`;
	const navigate = useNavigate();
	const isUserLoggedIn = useIsLoggedIn();
	const isOnJobPage = window.location.pathname.includes("/jobs/");

	// embedPage is true when we are on /companies/:id/embed page
	const embedPage = matchPath("/companies/:id/embed", window.location.pathname);

	const effectiveSurface: RoleExplainerSurface = surface ?? (data ? "job_drawer" : "job_details_page");

	// Custom hooks
	const { jobId, jobDetails, loading, applicationDetails, setApplicationDetails } = useJobDetails(data);
	const { saveJobInteraction } = useJobInteractions(jobId, data?._id);
	const analytics = useJobPageAnalytics(jobDetails, effectiveSurface);
	const { roleExplanations, explainerIsLoading, explainerHasError, handleRoleExplainer } = useRoleExplainer(
		jobDetails,
		{
			onLoaded: analytics.trackRoleExplainerViewed,
			onFailed: analytics.trackRoleExplainerFailed,
		},
	);
	const { isOnProfilePage: _isOnProfilePage, isInBasket: _isInBasket } = useFitCheckFlow(
		jobDetails,
		setShowJobDetailsModal,
	);
	const {
		showDSPModal,
		setShowDSPModal,
		showApplyModal,
		setShowApplyModal,
		scrollToEmail,
		applicationRef,
		onApply,
		handleDSPModalSelect,
		dspModalData,
	} = useJobApplication(applicationDetails, setApplicationDetails, isUserLoggedIn, jobId);

	// Role Explainer view state (localStorage)
	const { hasViewedRoleExplainer, markRoleExplainerViewed } = useRoleExplainerViewState(jobId);

	// Auto-load role explainer if user has previously viewed it.
	// Guarded per job: without the ref a failed run leaves roleExplanations null with
	// loading back to false, which re-triggers this effect indefinitely.
	// Held until the job itself has arrived, because the explainer posts the job body upstream.
	const { trackRoleExplainerAutoLoaded } = analytics;
	const autoLoadedJobRef = useRef<string | null>(null);
	useEffect(() => {
		if (!jobId || !jobDetails?._id || !hasViewedRoleExplainer) return;
		if (autoLoadedJobRef.current === jobId) return;
		if (roleExplanations || explainerIsLoading || explainerHasError) return;

		autoLoadedJobRef.current = jobId;
		trackRoleExplainerAutoLoaded();
		handleRoleExplainer("auto_reload");
	}, [
		jobId,
		jobDetails?._id,
		hasViewedRoleExplainer,
		roleExplanations,
		explainerIsLoading,
		explainerHasError,
		handleRoleExplainer,
		trackRoleExplainerAutoLoaded,
	]);

	// Mark role explainer as viewed when it loads successfully
	useEffect(() => {
		if (roleExplanations && !hasViewedRoleExplainer) {
			markRoleExplainerViewed();
		}
	}, [roleExplanations, hasViewedRoleExplainer, markRoleExplainerViewed]);

	// Feature flags
	const showPostGenerator = useFeatureFlag("posterGenerator");

	// Post generator modal
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleMenuClick = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);

	// Page-level language toggle for role guidance (Role Explainer + FitCheck)
	const [guidanceLanguage, setGuidanceLanguage] = useState<"en" | "sw">("en");

	// Whether the explanation is open in its accordion. The job description hides behind a
	// summary while it is, so this state is what lets the posting come back when it is folded.
	const [explainerExpanded, setExplainerExpanded] = useState(true);

	// FitCheck compares the reader's own profile against the role, so an anonymous reader has to
	// sign in first. ref points at this job's own page rather than at whichever page hosted the
	// drawer — signing in from a company page would otherwise drop them back on the company,
	// with the role they were interested in nowhere in sight.
	const handleFitCheckClick = () => {
		analytics.trackFitCheckStarted();
		if (isUserLoggedIn) return;

		const companyName = jobDetails?.company?.company_name;
		const ref =
			jobDetails?._id && jobDetails?.jobTitle && companyName
				? `/jobs/${slugifyURL({ _id: jobDetails._id, companyName, jobTitle: jobDetails.jobTitle })}`
				: window.location.pathname + window.location.search;

		navigate(`/signin?ref=${encodeURIComponent(ref)}`);
	};

	// Navigation
	const handleBackButton = () => {
		if (isOnJobPage) {
			navigate("/jobs");
		} else if (setShowJobDetailsModal) {
			setShowJobDetailsModal(false);
		}
	};

	return (
		<>
			{loading && (
				<div className="flex items-center justify-center min-h-screen">
					<Loader />
				</div>
			)}
			{!loading && (
				<>
					<div className="min-h-screen">
						<Helmet>
							<title>
								{jobDetails?.jobTitle} job at {jobDetails?.company?.company_name || ""} in{" "}
								{jobDetails?.jobLocation?.region}, Tanzania
							</title>
							<meta name="description" content={stripHTML(jobDetails?.jobDescription?.lookingToHire)} />
							<meta
								name="keywords"
								content={`${jobDetails?.jobTitle} job, ${jobDetails?.company?.company_name || ""}, ${jobDetails?.jobLocation?.region} Tanzania`}
							/>
							<meta property="og:title" content={jobDetails?.jobTitle} />
							<meta property="og:description" content={stripHTML(jobDetails?.jobDescription?.lookingToHire)} />
							<meta property="og:image" content={jobDetails?.company?.logo_url} />
							<meta property="og:url" content={window.location.href} />
						</Helmet>

						<div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-8">
							<JobHeader
								jobDetails={jobDetails}
								baseUrl={baseUrl}
								onBack={handleBackButton}
								onShare={() => saveJobInteraction("Shared")}
							/>

							<div className="mb-8">
								<div>
									<JobMetadata
										jobDetails={jobDetails}
										showPostGenerator={showPostGenerator}
										onMenuClick={handleMenuClick}
										onSave={() => {
											saveJobInteraction("Saved");
											analytics.trackJobSaved();
										}}
										onShare={() => {
											saveJobInteraction("Shared");
											analytics.trackJobShared();
										}}
										onNotRelevant={() => {
											saveJobInteraction("Irrevant");
											analytics.trackJobMarkedIrrelevant();
										}}
										isExpired={isJobExpired(jobDetails.closeAt)}
									/>

									{/* CTA Section - Role Explainer */}
									{!isJobExpired(jobDetails.closeAt) && (
										<JobActionButtons
											explainerIsLoading={explainerIsLoading}
											roleExplanations={roleExplanations}
											onRoleExplainer={() => {
												handleRoleExplainer("click");
												analytics.trackRoleExplainerOpened("click");
											}}
										/>
									)}
								</div>

								{/* Main content */}
								<div className="pt-3">
									{/* 1. Role Explainer Section - show when loading, has content, or failed */}
									{(explainerIsLoading || roleExplanations || explainerHasError) && (
										<RoleExplainerSection
											roleExplanations={roleExplanations}
											isLoading={explainerIsLoading}
											hasError={explainerHasError}
											onExplain={() => {
												handleRoleExplainer("retry");
												analytics.trackRoleExplainerOpened("retry");
											}}
											lang={guidanceLanguage}
											onLangChange={setGuidanceLanguage}
											onExpandedChange={setExplainerExpanded}
										/>
									)}

									{/* 2. Job Description - collapsed only while the explanation is open on screen,
									    so folding the explainer away brings the original posting back into view. */}
									<JobDescription
										jobDetails={jobDetails}
										isCollapsedByDefault={hasViewedRoleExplainer && roleExplanations !== null && explainerExpanded}
									/>

									{/* 3. FitCheck Section - only show if role explainer has been viewed.
									    Sits between the posting and the apply steps: read the role, then check
									    your fit, then apply. */}
									{!embedPage && hasViewedRoleExplainer && (
										<FitCheckSection
											isLoggedIn={isUserLoggedIn}
											jobDetails={jobDetails}
											hasFitCheckResult={false}
											onFitCheckClick={handleFitCheckClick}
											onApplyClick={() => onApply(applicationDetails.link)}
											lang={guidanceLanguage}
										/>
									)}

									<div className="">
										{/* 4. Commitment Zone - Apply section */}
										<ApplicationProcess
											jobDetails={jobDetails}
											applicationDetails={applicationDetails}
											scrollToEmail={scrollToEmail}
											applicationRef={applicationRef}
											onApply={onApply}
											onSave={() => saveJobInteraction("Saved")}
											hasViewedExplainer={roleExplanations !== null}
											hasCompletedFitCheck={false}
											onExplore={() => {
												// Navigate to jobs filtered by same category
												const category = (jobDetails as { jobExpertise?: string }).jobExpertise;
												if (category) {
													navigate(`/jobs?category=${encodeURIComponent(category)}`);
												} else {
													navigate("/jobs");
												}
											}}
										/>
									</div>

									{/* 5. Other live roles at the same employer. Standalone page only: the drawer
									    is opened from a list that already shows them, and it has nowhere to
									    navigate to without closing itself. */}
									{effectiveSurface === "job_details_page" && !embedPage && (
										<MoreRolesFromCompany
											companyId={jobDetails?.company?._id}
											companyName={jobDetails?.company?.company_name}
											companyLogo={jobDetails?.company?.logo_url}
											currentJobId={jobDetails?._id}
										/>
									)}
								</div>
							</div>

							{/* Company Card */}
							{!embedPage && <CompanyCard jobDetails={jobDetails} />}

							{/* More Roles CTA */}
							{!embedPage && (
								<div className="mt-8 py-6 border-t border-gray-200 text-center">
									<Link
										to={`/jobs${(jobDetails as { jobExpertise?: string })?.jobExpertise ? `?category=${encodeURIComponent((jobDetails as { jobExpertise?: string }).jobExpertise || "")}` : ""}`}
										className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
									>
										Explore more {(jobDetails as { jobExpertise?: string })?.jobExpertise || ""} roles
										<ChevronRight className="h-4 w-4" />
									</Link>
								</div>
							)}

							{/* Apply Modal */}
							<ApplyModal
								isOpen={showApplyModal}
								onClose={() => setShowApplyModal(false)}
								jobTitle={jobDetails.jobTitle}
								companyName={(jobDetails as { companyName?: string }).companyName}
								applicationDetails={applicationDetails}
								onDrawer={true}
							/>

							<DSPModal
								data={dspModalData}
								isOpen={showDSPModal}
								onSubmit={handleDSPModalSelect}
								onClose={() => setShowDSPModal(false)}
								action={(dspModalData as { action?: string })?.action}
							/>
						</div>
					</div>

					{/* Post Generator Modal */}
					<Modal
						isOpen={isModalOpen}
						onClose={handleCloseModal}
						title="Generate Image"
						description=""
						className=""
						titleClassName=""
					>
						<GenerateSingleJobImage
							companyName={jobDetails?.company?.company_name || ""}
							jobTitle={jobDetails?.jobTitle}
							jobDescription={stripHTML(jobDetails?.jobDescription?.lookingToHire).split(".")[0]}
							qualification={
								toLowerCaseCapitalize(jobDetails?.educationQualification?.minEducationLevel) +
									" in " +
									(jobDetails?.educationQualification?.relevantMajors?.join(", ") || "") || ""
							}
							deadline={dayjs(jobDetails?.closeAt).format("MMM DD, YYYY")}
							jobLocation={`${jobDetails?.jobLocation?.region}`}
							jobLink={`www.dproz.com/companies/${jobDetails?.company?._id}`}
						/>
					</Modal>
				</>
			)}
		</>
	);
}

export default ViewJob;
